import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Combobox from "@/components/Combobox";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Link2, Loader2, Truck } from "lucide-react";

function TargaRow({ t, subvettori, onDone }) {
  const [mode, setMode] = useState("esistente");
  const [sid, setSid] = useState("");
  const [rs, setRs] = useState("");
  const [piva, setPiva] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { targa: t.targa };
      if (mode === "esistente") payload.subvettore_id = sid;
      else Object.assign(payload, { ragione_sociale: rs, partita_iva: piva });
      const { data } = await api.post("/viaggi/collega-subvettore", payload);
      toast.success(`${t.targa} collegata a ${data.ragione_sociale}: ${data.viaggi_aggiornati} viaggi aggiornati`);
      onDone(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const ok = mode === "esistente" ? !!sid : rs.trim().length > 1;
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid={`targa-row-${t.targa}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="h-4 w-4 text-amber-600" />
          <span className="font-mono font-semibold text-slate-800">{t.targa}</span>
          <span className="text-xs text-slate-500">{t.count} viaggi · ultimo {formatDate(t.ultimo_viaggio)}</span>
        </div>
        <div className="flex gap-1 text-xs">
          <button type="button" onClick={() => setMode("esistente")} data-testid={`mode-esistente-${t.targa}`}
            className={`px-2 py-0.5 rounded-full border ${mode === "esistente" ? "bg-sky-600 text-white border-sky-600" : "border-slate-300 text-slate-600"}`}>Sub-vettore esistente</button>
          <button type="button" onClick={() => setMode("nuovo")} data-testid={`mode-nuovo-${t.targa}`}
            className={`px-2 py-0.5 rounded-full border ${mode === "nuovo" ? "bg-sky-600 text-white border-sky-600" : "border-slate-300 text-slate-600"}`}>Nuovo sub-vettore</button>
        </div>
      </div>
      <div className="flex items-end gap-2">
        {mode === "esistente" ? (
          <div className="flex-1"><Combobox options={subvettori} value={sid} onChange={setSid} placeholder="Seleziona sub-vettore..." testid={`sv-select-${t.targa}`} /></div>
        ) : (
          <>
            <div className="flex-1"><Label className="text-xs">Ragione sociale *</Label><Input className="mt-1 h-9" value={rs} onChange={(e) => setRs(e.target.value)} data-testid={`sv-nuovo-rs-${t.targa}`} /></div>
            <div className="w-44"><Label className="text-xs">Partita IVA</Label><Input className="mt-1 h-9" value={piva} onChange={(e) => setPiva(e.target.value)} data-testid={`sv-nuovo-piva-${t.targa}`} /></div>
          </>
        )}
        <Button size="sm" className="h-9" disabled={!ok || saving} onClick={save} data-testid={`sv-collega-${t.targa}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />}Collega
        </Button>
      </div>
    </div>
  );
}

export default function CollegaSubvettoreDialog({ open, onOpenChange, onLinked }) {
  const [targhe, setTarghe] = useState([]);
  const [subvettori, setSubvettori] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/viaggi/targhe-non-collegate"), api.get("/subvettori/options")])
      .then(([t, s]) => { setTarghe(t.data); setSubvettori(s.data); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); }, [open]);

  const done = () => { load(); onLinked?.(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="collega-subvettore-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Collega targhe esterne a sub-vettori</DialogTitle>
          <DialogDescription>
            Targhe presenti nei viaggi ma non in anagrafica mezzi e senza sub-vettore. Collegandole, la targa viene aggiunta ai mezzi del sub-vettore e tutti i viaggi con quella targa vengono attribuiti (anche futuri import).
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-slate-500 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Caricamento...</div>
        ) : targhe.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm" data-testid="targhe-empty">Nessuna targa da collegare.</div>
        ) : (
          <div className="space-y-2">{targhe.map((t) => <TargaRow key={t.targa} t={t} subvettori={subvettori} onDone={done} />)}</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
